import React, { useState } from 'react'
import { ScissorsIcon, XIcon } from 'lucide-react'
import { splitConversationAtMessage } from '../../api/inboxSplit'

interface SplitConversationModalProps {
  isOpen: boolean
  conversationId: string
  messageId: string
  messagePreview: string
  onClose: () => void
  onSplit: (newConversationId: string) => void
}

export function SplitConversationModal({
  isOpen,
  conversationId,
  messageId,
  messagePreview,
  onClose,
  onSplit,
}: SplitConversationModalProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  if (!isOpen) return null

  async function handleSplit() {
    setLoading(true)
    setError('')
    try {
      const newId = await splitConversationAtMessage(conversationId, messageId)
      onSplit(newId)
      onClose()
    } catch (err: any) {
      setError(err.message || 'Split failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <ScissorsIcon className="w-5 h-5 text-gray-500" />
            Split conversation here
          </h3>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <XIcon className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            A new thread will start at this message. Earlier messages stay in the original conversation.
            Open tasks remain on the original thread.
          </p>
          <div className="p-3 bg-gray-50 rounded-lg text-xs text-gray-700 border border-gray-200">
            {messagePreview.slice(0, 240)}
            {messagePreview.length > 240 ? '…' : ''}
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              disabled={loading}
              onClick={handleSplit}
              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Splitting…' : 'Split conversation'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
